import { useCallback, useMemo, useLayoutEffect, useRef } from "react";
import { moduleForPathname } from "./screenModules";
import { boundedVisibleDataContext } from "./visibleDataContext";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const positiveId = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const safeRead = (storage, key) => {
  try {
    return JSON.parse(storage?.getItem(key) || "null");
  } catch (error) {
    return null;
  }
};

const dateHint = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const sectionForPathname = (pathname = "/") => (
  moduleForPathname(pathname).section
);

const branchFromSelection = (selection) => {
  if (!Array.isArray(selection) || selection.length !== 1) return null;
  const selected = selection[0];
  const isBranch = selected?.level === "branch_offices" ||
    (!selected?.level && Boolean(selected?.names_parents?.office_name));
  return isBranch ? positiveId(selected.id) : null;
};

const selectedDevice = (state) => {
  const devices = state?.devicesSelected;
  if (!Array.isArray(devices) || devices.length !== 1) return null;
  return devices[0];
};

const deviceIdFromState = (state, authorizedDevices, branchId) => {
  const selected = selectedDevice(state);
  const selectedId = positiveId(selected?.value ?? selected?.id ?? selected?.data?.id);
  if (!selectedId || !Array.isArray(authorizedDevices)) return null;
  const authorized = authorizedDevices.find((device) => positiveId(device?.id) === selectedId);
  if (!authorized) return null;
  if (branchId && positiveId(authorized.branch_fk) !== branchId) return null;
  return selectedId;
};

const branchFromDeviceState = (state, authorizedDevices) => {
  const selected = selectedDevice(state);
  const selectedId = positiveId(selected?.value ?? selected?.id ?? selected?.data?.id);
  if (!selectedId || !Array.isArray(authorizedDevices)) return null;
  const authorized = authorizedDevices.find((device) => positiveId(device?.id) === selectedId);
  return positiveId(authorized?.branch_fk);
};

const addPeriod = (context, source) => {
  const startDate = dateHint(source?.startDate ?? source?.periodo?.startDate);
  const endDate = dateHint(source?.endDate ?? source?.periodo?.endDate);
  if (startDate && endDate) return { ...context, startDate, endDate };
  return context;
};

export const buildScreenContext = ({ pathname, storage, authorizedDevices, visibleContext = {} }) => {
  const section = sectionForPathname(pathname);
  let context = { ...moduleForPathname(pathname), screen_context_source: "current_router",
    visible_context: boundedVisibleContext(visibleContext) };
  const visibleData = boundedVisibleDataContext(visibleContext.getVisibleDataContext
    ? visibleContext.getVisibleDataContext() : visibleContext.visible_data_context);
  if (visibleData) context.visible_data_context = visibleData;
  const locations = safeRead(storage, "measurement_firstSelect");
  const consumption = safeRead(storage, "consumption_state");
  const passiveLoad = safeRead(storage, "passiveLoad_state");
  const billing = safeRead(storage, "FilterEbm");

  const usesMeasurementSelection = ["consumption", "demand", "reactive"].includes(section);
  let branchId = usesMeasurementSelection ? branchFromSelection(locations) : null;

  const isPassiveLoad = /^\/analysis\/passive-load(?:\/|$)/i.test(pathname);
  const isPersistedConsumption = /^\/(?:consumption|maximetro)(?:\/|$)/i.test(pathname) ||
    /^\/analysis\/heatmap(?:\/|$)/i.test(pathname);

  const deviceState = visibleContext.devicesSelected ? visibleContext :
    (isPassiveLoad ? passiveLoad : (isPersistedConsumption ? consumption : null));
  if (visibleContext.firstSelect) branchId = branchFromSelection(visibleContext.firstSelect);
  branchId = branchId || branchFromDeviceState(deviceState, authorizedDevices);
  if (branchId) context.branchId = branchId;

  const deviceId = deviceIdFromState(deviceState, authorizedDevices, branchId);
  if (deviceId) context.deviceId = deviceId;

  if (isPersistedConsumption) context = addPeriod(context, consumption);
  if (isPassiveLoad) context = addPeriod(context, passiveLoad);
  if (section === "billing") context = addPeriod(context, billing);
  if (Object.prototype.hasOwnProperty.call(visibleContext, "startDate")) {
    delete context.startDate;
    delete context.endDate;
  }
  context = addPeriod(context, visibleContext);
  const detailId = pathname.match(/\/(\d+)\/?$/)?.[1];
  if (detailId && context.module_code === "improvement_projects") context.visible_context.selected_project = detailId;
  if (detailId && ["recommendations", "agent_recommendations"].includes(context.module_code)) {
    context.visible_context.selected_recommendation = detailId;
  }

  return context;
};

const visibleKeys = new Set(["active_tab", "selected_metric", "selected_unit", "selected_chart",
  "selected_invoice", "selected_project", "selected_recommendation", "selected_parameter",
  "selected_task", "status", "progress", "provider", "comparison_start", "comparison_end", "frequency"]);
export const boundedVisibleContext = (value = {}) => Object.fromEntries(
  Object.entries(value).filter(([key, item]) => visibleKeys.has(key) &&
    (typeof item === "string" || typeof item === "boolean" || (typeof item === "number" && Number.isFinite(item))))
    .slice(0, 16).map(([key, item]) => [key, typeof item === "string" ? item.slice(0, 200) : item])
);
const mountedScreens = new Map();
// Pages register small semantic selections, never rows, series, HTML or permissions.
export const useConsultantVisibleContext = (value) => {
  const { pathname } = useLocation();
  const latest = useRef(value);
  latest.current = value;
  useLayoutEffect(() => {
    const getter = () => latest.current;
    mountedScreens.set(pathname, getter);
    return () => { if (mountedScreens.get(pathname) === getter) mountedScreens.delete(pathname); };
  }, [pathname]);
};

export const useScreenContextAdapter = () => {
  const location = useLocation();
  const authorizedDevices = useSelector((state) => state.devices?.currentDevices);
  const section = useMemo(() => sectionForPathname(location.pathname), [location.pathname]);
  const getCurrentContext = useCallback(() => buildScreenContext({
    pathname: location.pathname,
    storage: typeof window !== "undefined" ? window.localStorage : null,
    authorizedDevices,
    visibleContext: mountedScreens.get(location.pathname)?.() || {},
  }), [location.pathname, authorizedDevices]);

  return { section, getCurrentContext };
};

